import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import {
  AvatarFallback,
  AvatarImage,
  Avatar as AvatarPrimitive,
  generateColorFromCharacter,
  getInitials,
} from "@/components/ui/avatar/avatar-primitive";

const avatarVariants = cva("text-xs leading-4 font-medium text-text-light", {
  variants: {
    size: {
      xs: "size-4",
      sm: "size-5",
      base: "size-6",
      md: "size-8 text-sm leading-5 font-medium",
      lg: "size-10 font-medium",
      xl: "size-14 text-xl leading-7 font-medium",
      "2xl": "size-24 text-3xl leading-9 font-medium",
      "3xl": "size-32 text-5xl leading-none font-medium",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type AvatarProps = {
  src: string;
  name: string;
  className?: string;
} & VariantProps<typeof avatarVariants>;

export default function Avatar({ src, name, size, className }: AvatarProps) {
  const imageSrc = src
    ? `${src}${src.includes("?") ? "&" : "?"}variant=thumbnail`
    : src;

  return (
    <AvatarPrimitive className={cn(avatarVariants({ size }), className)}>
      <AvatarImage src={imageSrc} alt={name} />
      {!src && (
        <AvatarFallback
          style={{
            backgroundColor: generateColorFromCharacter(name),
          }}
        >
          {getInitials(name, true)}
        </AvatarFallback>
      )}
    </AvatarPrimitive>
  );
}
